/* eslint-disable react/jsx-one-expression-per-line */
import { CalendarOutlined, CarryOutTwoTone } from '@ant-design/icons';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Col, Row, theme } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { PreviewInvoiceType } from '../OrderSummaryV2/enums/PreviewInvoiceType';

const { useToken } = theme;

export default function BillingTextV2({
    previewType,
    isAddonsChanged,
    isPlanChanged,
    subscriptionInfo,
    isPartnerClient,
    hideComparePlansLink,
    isInBetweenDowngradePeriod,
}) {
    useLingui();
    const { token } = useToken();

    const {
        trialEndsAt,
        billingCycleAnchor,
        billingCycle,
    } = subscriptionInfo || {};

    const isNotChanged = !isAddonsChanged && !isPlanChanged;

    const getChangedItemType = (isPlanChanged, isAddonsChanged) => {
        if (isPlanChanged && isAddonsChanged)
            return 'all';
        if (isPlanChanged && !isAddonsChanged)
            return 'plan';
        if (!isPlanChanged && isAddonsChanged)
            return 'addon';
        return null;
    };

    const getChangedItemText = (isPlanChanged, isAddonsChanged) => {
        const changedItemType = getChangedItemType(isPlanChanged, isAddonsChanged);
        switch (changedItemType) {
            case 'all':
                return <Trans>plan & addons</Trans>;
            case 'plan':
                return <Trans>plan</Trans>;
            case 'addon':
                return <Trans>addons</Trans>;
            default:
                return <Trans>subscription</Trans>;
        }
    };

    const getBillingText = previewType => {
        // NO CHANGE IN SUBSCRIPTION COMBO
        if (isNotChanged) {
            // revert back to current subscription (from upcoming subscription)
            // eg: PREMIUM -> LAUNCH ----> PREMIUM
            if (isInBetweenDowngradePeriod) {
                const subscriptionMessage = isPartnerClient
                    ? "You are changing back to your client's current subscription."
                    : "You are changing back to your current subscription.";

                if (isPartnerClient) {
                    return (
                        <>
                            <CarryOutTwoTone twoToneColor={token.colorSuccess} className="blinking" />
                            &nbsp;
                            <Trans>
                                {subscriptionMessage}
                            </Trans>
                        </>
                    );
                }

                return (
                    <>
                        <CarryOutTwoTone twoToneColor={token.colorSuccess} className="blinking" />
                        &nbsp;
                        <Trans>
                            {subscriptionMessage}&nbsp;
                            Changes will take effect immediately.
                            <br />
                            Same charges will be billed on your next invoice.
                        </Trans>
                    </>
                );
            }
            return null;
        }

        const changedItemText = getChangedItemText(isPlanChanged, isAddonsChanged);
        if (isPartnerClient)
            return null;

        switch (previewType) {
            case PreviewInvoiceType.SUBSCRIBE_AFTER_TRIAL:
                return (
                    <>
                        <CarryOutTwoTone twoToneColor={token.colorSuccess} className="blinking" />
                            &nbsp;
                        <Trans>
                            You will enjoy your subscription immediately upon successful payment.
                        </Trans>
                    </>
                );
            case PreviewInvoiceType.SUBSCRIBE_DURING_TRIAL:
            case PreviewInvoiceType.SWITCH_DURING_ACTIVE_TRIAL: {
                const nextBillingDate = dayjs(trialEndsAt)
                    .format('DD/MM/YYYY, hh:mm A');
                return (
                    <>
                        <CalendarOutlined style={{ color: token.colorTextTertiary }} className="blinking" />
                            &nbsp;
                        <Trans>
                            You will be billed at&nbsp;
                            <strong>{nextBillingDate}</strong> for your selected {changedItemText}.
                            <br />
                            Continue to enjoy Elite Free Trial until then.
                        </Trans>
                    </>
                );
            }
            case PreviewInvoiceType.SWITCH_SAME_CYCLE_UPGRADE:
            case PreviewInvoiceType.SWITCH_SAME_CYCLE_SAME:
                return (
                    <>
                        <CarryOutTwoTone twoToneColor={token.colorSuccess} className="blinking" />
                            &nbsp;
                        <Trans>
                            You will enjoy your updated&nbsp;
                            {changedItemText}&nbsp;immediately.&nbsp;
                            Prorated charges will be billed immediately.
                        </Trans>
                    </>
                );
            case PreviewInvoiceType.SWITCH_DIFF_BILLING_CYCLE: // except to LAUNCH or any free plans eg ZERO
                return (
                    <>
                        <CarryOutTwoTone twoToneColor={token.colorSuccess} className="blinking" />
                            &nbsp;
                        <Trans>
                            You will enjoy your updated&nbsp;
                            {changedItemText}&nbsp;immediately.&nbsp;
                            Prorated charges (if any) will be billed immediately.
                        </Trans>
                    </>
                );
            case PreviewInvoiceType.SWITCH_DIFF_BILLING_CYCLE_TO_FREE_PLAN: // added by marcus for ENG-3635
            case PreviewInvoiceType.SWITCH_SAME_CYCLE_DOWNGRADE: {
                let nextBillingDayJs = dayjs(billingCycleAnchor);

                // For monthly billing cycle
                if (billingCycle === 1) {
                    // SET the day of the month from the billing cycle anchor
                    nextBillingDayJs = nextBillingDayJs.set('date', dayjs(billingCycleAnchor).date());
                    nextBillingDayJs = nextBillingDayJs.set('month', dayjs().month());
                    nextBillingDayJs = nextBillingDayJs.set('year', dayjs().year());

                    // If it's before the current date, add one month
                    if (nextBillingDayJs.isBefore(dayjs()))
                        nextBillingDayJs = nextBillingDayJs.add(1, 'month');
                }
                else if (billingCycle === 12) {
                    // For annual billing cycle
                    // SET the day AND month from the billing cycle anchor
                    nextBillingDayJs = nextBillingDayJs.set('date', dayjs(billingCycleAnchor).date());
                    nextBillingDayJs = nextBillingDayJs.set('month', dayjs(billingCycleAnchor).month());
                    nextBillingDayJs = nextBillingDayJs.set('year', dayjs().year());

                    // If it's before the current date, add one year
                    if (nextBillingDayJs.isBefore(dayjs()))
                        nextBillingDayJs = nextBillingDayJs.add(12, 'month');
                }
                const nextBillingDate = nextBillingDayJs.format('DD/MM/YYYY, hh:mm A');

                return (
                    <>
                        <CalendarOutlined style={{ color: token.colorTextTertiary }} className="blinking" />
                            &nbsp;
                        <Trans>
                            You will be billed and switched to your new subscription at&nbsp;
                            <strong>{nextBillingDate}</strong>.
                            <br />
                            Continue to enjoy your current subscription until then.
                        </Trans>
                    </>
                );
            }

            default:
                return null;
        }
    };

    const renderCompareLink = () => {
        const parterDiscountText = isPartnerClient && (
            <>
                {/* <Trans>Your payment card will be charged with the partner discounted rate.</Trans> */}
                <Trans>You are entitled to partner discount.</Trans>&nbsp;
            </>
        );
        return (
            <div>
                {parterDiscountText}
                <Trans>
                    Compare our latest pricing plans&nbsp;
                    <a href="https://bukku.my/pricing" target="_blank" rel="noopener noreferrer">
                        here
                    </a>
                    .
                </Trans>
            </div>
        );
    };

    return (
        <Row gutter={[0, 4]} style={{marginTop: '12px'}}>
            {
                !hideComparePlansLink ?
                    <Col span={24} style={{fontSize: '12px'}}>
                        {renderCompareLink()}
                    </Col> :
                    null
            }
            <Col span={24} style={{fontSize: '12px'}}>
                {getBillingText(previewType)}
            </Col>
        </Row>
    );
}
